import { useCallback, useEffect, useRef } from "react";
import { useCartEventEmitter, useCartState } from "@made-people/grease-cart";
import { useMmBackendClient } from "../../hooks/use-mm-backend-client";
import { useVisitorEmail } from "./mm-visitor-email-provider";
import type { Cart } from "@made-people/grease-cart";

const encodeCartContents = (cart: Cart) =>
  btoa(
    JSON.stringify({
      i: cart.items.map((item) => [item.id, item.quantity]),
    }),
  );

let lastCartSyncKey: string | undefined;

export const useSyncEmarsysContactCart = () => {
  const client = useMmBackendClient();
  const clientRef = useRef(client);
  clientRef.current = client;

  return useCallback(
    (email: string, cart: Cart) => {
      const cartContents = encodeCartContents(cart);
      const syncKey = `${email}:${cartContents}`;
      if (lastCartSyncKey === syncKey) {
        return Promise.resolve();
      }
      lastCartSyncKey = syncKey;

      return clientRef.current
        .put("/emarsys/contact/cart", {
          email,
          cartContents,
        })
        .then(() => undefined)
        .catch((error: unknown) => {
          if (lastCartSyncKey === syncKey) {
            lastCartSyncKey = undefined;
          }
          throw error;
        });
    },
    [],
  );
};

export const EmarsysContactCartSync = () => {
  const email = useVisitorEmail();
  const { cart } = useCartState();
  const { eventEmitter } = useCartEventEmitter();
  const sendCart = useSyncEmarsysContactCart();
  const emailRef = useRef(email);
  emailRef.current = email;

  const syncCart = useCallback((cartToSync: Cart) => {
    const currentEmail = emailRef.current;
    if (!currentEmail) {
      return;
    }

    sendCart(currentEmail, cartToSync).catch((error: unknown) => {
      console.error("Emarsys contact cart sync failed:", error);
    });
  }, [sendCart]);

  useEffect(() => {
    const syncCartFromEvent = (params: { cart: Cart }) => {
      syncCart(params.cart);
    };

    eventEmitter.on("addToCart", syncCartFromEvent);
    eventEmitter.on("removeFromCart", syncCartFromEvent);
    eventEmitter.on("updateQuantity", syncCartFromEvent);
    return () => {
      eventEmitter.off("addToCart", syncCartFromEvent);
      eventEmitter.off("removeFromCart", syncCartFromEvent);
      eventEmitter.off("updateQuantity", syncCartFromEvent);
    };
  }, [eventEmitter, syncCart]);

  useEffect(() => {
    if (!email || !cart) {
      return;
    }
    syncCart(cart);
  }, [cart, email, syncCart]);

  return null;
};
